import React, { useState, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import CandidateTableFilter from '../../../components/candidates-table/CandidateTableFilter';
import CandidatesTable from '../../../components/candidates-table/CandidatesTable';
import { AssignedCandidate } from '../../../models/assigned-candidate.interface';
import { useTranslation } from 'react-i18next';
import {
  getAssignedCandidates,
  getCompanyCandidateFilterOptions,
  setUserStatusSort,
  setDurationSort,
  setExperienceSort,
  setNameSort,
  setPositionSort,
  setAIMatchSort,
  setOfferFilter,
  setStatusFilter,
  setLanguageIdsFilter,
  setSkillIdsFilter,
  setCitiesFilter,
  setUnseenFilter,
  setMinMatchFilter,
  setDaysAgoFilter,
} from '../../../store/slices/companyCandidatesSlice';

const ApplicantsCompany = () => {
  const { t } = useTranslation();
  const candidates = useSelector(
    (state: RootState) => state.companyCandidatesReducer.assignedCandidates
  );
  const [displayedCandidates, setDisplayedCandidates] = useState<
    AssignedCandidate[]
  >([]);
  const dispatch = useDispatch<AppDispatch>();

  const [statusValue, setStatusValue] = useState<string>('');
  const [durationSortDescending, setDurationSortDescending] =
    useState<boolean>(true);
  const [nameSortDescending, setNameSortDescending] = useState<boolean>(false); // false to sort by ascending on first click
  const [vacancyNameSortDescending, setVacancyNameSortDescending] =
    useState<boolean>(false); // false to sort by ascending on first click
  const [matchSortDescending, setMatchSortDescending] = useState<boolean>(true);
  const [experienceSortDescending, setExperienceSortDescending] =
    useState<boolean>(true);
  const [userStatusSortDescending, setUserStatusSortDescending] =
      useState<boolean>(true);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [maxAmount, setMaxAmount] = useState<number>(10);

  // Advanced filter state
  const [selectedLanguageIds, setSelectedLanguageIds] = useState<string[]>([]);
  const [selectedSkillIds, setSelectedSkillIds] = useState<string[]>([]);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [unseenOnly, setUnseenOnly] = useState<boolean>(false);
  const [minMatch, setMinMatch] = useState<number | null>(null);
  const [daysAgo, setDaysAgo] = useState<number | null>(null);

  useEffect(() => {
    dispatch(
      getAssignedCandidates({
        pageNumber: 1,
        searchValue,
        size: maxAmount,
        statusValue,
      })
    );
    dispatch(getCompanyCandidateFilterOptions());
  }, []);

  useEffect(() => {
    setDisplayedCandidates(candidates);
  }, [candidates]);

  function sortByName(): void {
    uncheckAllRows();
    setPageNumber(1);
    const direction = nameSortDescending ? 'desc' : 'asc'; // first click: desc (flip if you prefer asc first)
    dispatch(setNameSort(direction));
    dispatch(
      getAssignedCandidates({
        pageNumber: 1,
        searchValue: searchValue,
        size: maxAmount,
        statusValue: statusValue,
        name: direction,
      })
    );

    setNameSortDescending(prev => !prev);
  }

  function sortByVacancyName(): void {
    uncheckAllRows();
    setPageNumber(1);
    const direction = vacancyNameSortDescending ? 'desc' : 'asc';
    dispatch(setPositionSort(direction));
    dispatch(
      getAssignedCandidates({
        pageNumber: 1,
        searchValue: searchValue,
        size: maxAmount,
        statusValue: statusValue,
        position: direction,
      })
    );

    setVacancyNameSortDescending(prev => !prev);
  }

  function sortByMatch(): void {
     uncheckAllRows();
     setPageNumber(1);
     const direction = matchSortDescending ? 'desc' : 'asc';
     dispatch(setAIMatchSort(direction));
     dispatch(
       getAssignedCandidates({
         pageNumber: 1,
         searchValue: searchValue,
         size: maxAmount,
         statusValue: statusValue,
         aiMatch: direction,
       })
     );

    setMatchSortDescending(prev => !prev);
  }

  function sortByLastPosition(): void {
    uncheckAllRows();
    setPageNumber(1);
    const direction = experienceSortDescending ? 'desc' : 'asc';
    dispatch(setExperienceSort(direction));
    dispatch(getAssignedCandidates({
      pageNumber: 1,
      searchValue,
      size: maxAmount,
      statusValue,
      experience: direction, // Poorly named - this is their last position
    }));
    setExperienceSortDescending(prev => !prev);
  }

  function sortByDuration(): void {
    uncheckAllRows();
    setPageNumber(1);
    const direction = durationSortDescending ? 'desc' : 'asc';
    dispatch(setDurationSort(direction));
    dispatch(getAssignedCandidates({
      pageNumber: 1,
      searchValue,
      size: maxAmount,
      statusValue,
      duration: direction,
    }));
    setDurationSortDescending(prev => !prev);
  }

  function sortByUserStatus(): void {
    uncheckAllRows();
    setPageNumber(1);
    const direction = userStatusSortDescending ? 'desc' : 'asc';
    dispatch(setUserStatusSort(direction));
    dispatch(getAssignedCandidates({
      pageNumber: 1,
      searchValue,
      size: maxAmount,
      statusValue,
      userStatus: direction,
    }));
    setUserStatusSortDescending(prev => !prev);
  }

  function handleRowCheck(id: string): void {
    setDisplayedCandidates((prev) => {
      const copy: AssignedCandidate[] = JSON.parse(JSON.stringify(prev));
      const selectedElementIndex = copy.findIndex((item) => item.id === id);
      copy[selectedElementIndex].checked = !copy[selectedElementIndex].checked;

      return copy;
    });
  }

  function onToggleLanguage(id: string): void {
    const next = selectedLanguageIds.includes(id)
      ? selectedLanguageIds.filter((x) => x !== id)
      : [...selectedLanguageIds, id];
    setSelectedLanguageIds(next);
    dispatch(setLanguageIdsFilter(next));
    uncheckAllRows();
    setPageNumber(1);
    dispatch(getAssignedCandidates({ pageNumber: 1, searchValue, size: maxAmount, statusValue, languageIds: next }));
  }

  function onToggleSkill(id: string): void {
    const next = selectedSkillIds.includes(id)
      ? selectedSkillIds.filter((x) => x !== id)
      : [...selectedSkillIds, id];
    setSelectedSkillIds(next);
    dispatch(setSkillIdsFilter(next));
    uncheckAllRows();
    setPageNumber(1);
    dispatch(getAssignedCandidates({ pageNumber: 1, searchValue, size: maxAmount, statusValue, skillIds: next }));
  }

  function onToggleCity(c: string): void {
    const next = selectedCities.includes(c)
      ? selectedCities.filter((x) => x !== c)
      : [...selectedCities, c];
    setSelectedCities(next);
    dispatch(setCitiesFilter(next));
    uncheckAllRows();
    setPageNumber(1);
    dispatch(getAssignedCandidates({ pageNumber: 1, searchValue, size: maxAmount, statusValue, cities: next }));
  }

  function onToggleUnseen(): void {
    const next = !unseenOnly;
    setUnseenOnly(next);
    dispatch(setUnseenFilter(next));
    uncheckAllRows();
    setPageNumber(1);
    dispatch(getAssignedCandidates({ pageNumber: 1, searchValue, size: maxAmount, statusValue, unseen: next }));
  }

  function onSetMinMatch(v: number | null): void {
    setMinMatch(v);
    dispatch(setMinMatchFilter(v));
    uncheckAllRows();
    setPageNumber(1);
    dispatch(getAssignedCandidates({ pageNumber: 1, searchValue, size: maxAmount, statusValue, minMatch: v }));
  }

  function onSetDaysAgo(v: number | null): void {
    setDaysAgo(v);
    dispatch(setDaysAgoFilter(v));
    uncheckAllRows();
    setPageNumber(1);
    dispatch(getAssignedCandidates({ pageNumber: 1, searchValue, size: maxAmount, statusValue, daysAgo: v }));
  }

  function filterByOffer(filterType: string): void {
    uncheckAllRows();
    setPageNumber(1);
    setSearchValue(filterType);
    setDisplayedCandidates(candidates);
    dispatch(setOfferFilter(filterType));
    dispatch(
      getAssignedCandidates({
        pageNumber: 1,
        searchValue: filterType,
        size: maxAmount,
        statusValue: statusValue,
      })
    );
  }

  function filterByStatus(filterStatus: string): void {
    uncheckAllRows();
    setPageNumber(1);
    setStatusValue(filterStatus);
    setDisplayedCandidates(candidates);
    dispatch(setStatusFilter(filterStatus));
    dispatch(
      getAssignedCandidates({
        pageNumber: 1,
        searchValue: searchValue,
        size: maxAmount,
        statusValue: filterStatus,
      })
    );
  }

  function uncheckAll(): void {
    setDisplayedCandidates((prev) => {
      const copy: AssignedCandidate[] = JSON.parse(JSON.stringify(prev));
      copy.map((item) => (item.checked = false));
      return copy;
    });
    setSelectAll(false);
  }

  function checkAll(): void {
    setDisplayedCandidates((prev) => {
      const copy: AssignedCandidate[] = JSON.parse(JSON.stringify(prev));
      copy.map((item) => (item.checked = true));
      return copy;
    });
    setSelectAll(true);
  }

  function uncheckAllRows(): void {
    setDisplayedCandidates((prev) => {
      const copy: AssignedCandidate[] = JSON.parse(JSON.stringify(prev));
      copy.map((item) => (item.checked = false));

      return copy;
    });

    setSelectAll(false);
  }

  function setMaxAmountHandler(value: number): void {
    setMaxAmount(value);
    uncheckAll();
    setPageNumber(1);
    dispatch(
      getAssignedCandidates({
        pageNumber: 1,
        searchValue,
        size: value,
        statusValue,
      })
    );
  }

  function setPageNumberHandler(page: number): void {
    setPageNumber(page);
    setSelectAll(false);
  }

  const columns = [
    {
      key: 'first_name',
      header: t('candidatesTablet.columns.name'),
      checkbox: true,
      sortFunction: sortByName,
    },
    {
      key: 'process',
      header: t('candidatesTablet.columns.vacancyName'),
      sortFunction: sortByVacancyName,
    },
    {
      key: 'match',
      header: t('candidatesTablet.columns.match'),
      sortFunction: sortByMatch,
    },
    {
      key: 'last_experience',
      header: t('candidatesTablet.columns.experience'),
      sortFunction: sortByLastPosition,
    },
    {
      key: 'durationOfLastJob',
      header: t('candidatesTablet.columns.duration'),
      sortFunction: sortByDuration,
    },
    {
      key: 'lastupdated',
      header: t('candidatesTablet.columns.lastUpdated'),
    },
    {
      key: 'viewprofile',
      header: t('candidatesTablet.columns.link.header'),
      type: 'link',
      linkTitle: t('candidatesTablet.columns.link.button'),
    },
    {
      key: 'user_type',
      header: t('candidatesTablet.columns.type'),
      sortFunction: sortByUserStatus,
    },
  ];

  return (
    <div className='zestur-card'>
      <div className='mt-5'>
        <CandidateTableFilter
          filterByStatus={filterByStatus}
          filterByOffer={filterByOffer}
          selectedLanguageIds={selectedLanguageIds}
          onToggleLanguage={onToggleLanguage}
          selectedSkillIds={selectedSkillIds}
          onToggleSkill={onToggleSkill}
          selectedCities={selectedCities}
          onToggleCity={onToggleCity}
          unseenOnly={unseenOnly}
          onToggleUnseen={onToggleUnseen}
          minMatch={minMatch}
          onSetMinMatch={onSetMinMatch}
          daysAgo={daysAgo}
          onSetDaysAgo={onSetDaysAgo}
        />
      </div>
      <CandidatesTable
        maxAmount={maxAmount}
        setAmount={setMaxAmountHandler}
        searchValue={searchValue}
        pageNumber={pageNumber}
        setPageNumberHandler={setPageNumberHandler}
        uncheckAll={uncheckAll}
        checkAll={checkAll}
        candidates={displayedCandidates}
        columns={columns}
        handleRowCheck={handleRowCheck}
        selectAll={selectAll}
        statusValue={statusValue}
      />
    </div>
  );
};

export default ApplicantsCompany;
